<script lang="ts">
import type { CheckboxEmits, CheckboxSlots, CheckboxProps as UCheckboxProps } from '#ui/types';

export interface CheckboxProps<T = boolean> extends UCheckboxProps<T> {
	readonly?: boolean;
}
</script>

<script setup lang="ts" generic="T = boolean">
import { reactiveOmit } from '@vueuse/core';
import { useForwardPropsEmits } from 'reka-ui';
import Checkbox from '#ui/components/Checkbox.vue';
import { useReadonly } from '../composables/use-readonly';
import { mergeUi } from '../utils/merge-ui';

defineOptions({ inheritAttrs: false });

const props = defineProps<CheckboxProps<T>>();
const emit = defineEmits<CheckboxEmits<T>>();
const slots = defineSlots<CheckboxSlots>();

const forwarded = useForwardPropsEmits(reactiveOmit(props, 'readonly'), emit);
const { preventKeyboardNavigation, preventPointer } = useReadonly(() => props.readonly ?? false);
</script>

<template>
	<div
		@pointerdown.capture="preventPointer"
		@click.capture="preventPointer"
		@keydown.capture="preventKeyboardNavigation"
	>
		<Checkbox
			v-bind="{ ...forwarded, ...$attrs }"
			:disabled="props.disabled"
			:aria-readonly="props.readonly || undefined"
			:ui="mergeUi(props.ui, { base: props.readonly ? 'opacity-100! cursor-default! pointer-events-none' : '' })"
		>
			<template v-for="(_, slotName) in slots" #[slotName]="slotData: any">
				<slot :name="slotName" v-bind="slotData" />
			</template>
		</Checkbox>
	</div>
</template>
